import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiArrowRight,
  FiCompass,
  FiCpu,
  FiGrid,
  FiLink2,
  FiPieChart,
  FiShield,
  FiTrendingUp,
} from 'react-icons/fi';
import { getMarketRegimeLabel, getMarketRiskLabel } from '../../components/market/marketRegimeLabels';
import './home-page.css';

const primaryActions = [
  {
    title: 'Dashboard',
    description: 'Vue tactique des signaux, du risque et des prioritÃƒÂ©s du moment.',
    path: '/dashboard',
    Icon: FiGrid,
    tone: 'cyan',
  },
  {
    title: 'Pattern Scanner',
    description: 'RepÃƒÂ©rez les structures multi-timeframe et retrouvez le snapshot Asset Scanner embarquÃƒÂ© sur chaque actif sÃƒÂ©lectionnÃƒÂ©.',
    path: '/scanner',
    Icon: FiCompass,
    tone: 'copper',
  },
  {
    title: 'Portefeuille',
    description: 'Regard consolidÃƒÂ© sur la valorisation, lÃ¢â‚¬â„¢exposition et lÃ¢â‚¬â„¢alignement macro.',
    path: '/unified-portfolio',
    Icon: FiPieChart,
    tone: 'green',
  },
  {
    title: 'MarchÃƒÂ©',
    description: 'Lecture du rÃƒÂ©gime, de la dominance et de la participation marchÃƒÂ©.',
    path: '/market',
    Icon: FiTrendingUp,
    tone: 'blue',
  },
  {
    title: 'Connexions',
    description: 'Pilotez vos exchanges et wallets sans sortir du cockpit.',
    path: '/connections',
    Icon: FiLink2,
    tone: 'slate',
  },
];

const workflowSteps = [
  {
    title: 'Lire le contexte',
    description: 'Commencez par le rÃƒÂ©gime marchÃƒÂ©, la dominance et le sentiment pour cadrer le terrain.',
    Icon: FiTrendingUp,
  },
  {
    title: 'Isoler les signaux',
    description: 'Passez du bruit ÃƒÂ  une short-list dÃ¢â‚¬â„¢actifs et de patterns rÃƒÂ©ellement actionnables.',
    Icon: FiCompass,
  },
  {
    title: 'Arbitrer le risque',
    description: 'Recoupez portefeuille, alignement macro et exÃƒÂ©cution avant de monter en taille.',
    Icon: FiShield,
  },
];

const stackHighlights = [
  'RÃƒÂ©gime et breadth marchÃƒÂ© consolidÃƒÂ©s',
  'Snapshot Asset Scanner embarquÃƒÂ© dans Pattern Scanner',
  'Portefeuille reliÃƒÂ© au contexte macro rÃƒÂ©el',
];

function fetchJson(url, signal) {
  return fetch(url, { signal }).then((response) => {
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
  });
}

function formatCompactNumber(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 'N/A';
  }
  return new Intl.NumberFormat('fr-FR', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(numericValue);
}

function formatUsdCompact(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 'N/A';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(numericValue);
}

function formatPercent(value, digits = 1) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 'N/A';
  }
  return `${numericValue.toFixed(digits)}%`;
}

function formatScore(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 'N/A';
  }
  const normalizedValue = Math.abs(numericValue) <= 1 ? numericValue * 100 : numericValue;
  return `${Math.round(normalizedValue)}/100`;
}

function formatRelativeDate(value) {
  if (!value) {
    return 'En attente de synchronisation';
  }
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return 'En attente de synchronisation';
  }
  return parsedDate.toLocaleString('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function humanizeRegime(regime) {
  if (!regime) {
    return 'Lecture en attente';
  }
  return getMarketRegimeLabel(regime);
}

function HomePage() {
  const [marketMetrics, setMarketMetrics] = useState(null);
  const [portfolioOverview, setPortfolioOverview] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    async function loadPulse() {
      const [marketResult, portfolioResult] = await Promise.allSettled([
        fetchJson('/api/market/metrics', controller.signal),
        fetchJson('/api/portfolio', controller.signal),
      ]);

      if (!active) {
        return;
      }

      if (marketResult.status === 'fulfilled') {
        setMarketMetrics(marketResult.value);
      }
      if (portfolioResult.status === 'fulfilled') {
        setPortfolioOverview(portfolioResult.value);
      }
    }

    loadPulse();

    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  const heroPulse = [
    {
      label: 'RÃƒÂ©gime marchÃƒÂ©',
      value: humanizeRegime(marketMetrics?.regime),
      detail: marketMetrics?.riskOnOff ? getMarketRiskLabel(marketMetrics.riskOnOff) : 'Qualification automatique du terrain',
    },
    {
      label: 'Macro score',
      value: formatScore(marketMetrics?.macroScore),
      detail: `BTC ${formatPercent(marketMetrics?.btcDominance, 1)}`,
    },
    {
      label: 'Sentiment',
      value: formatScore(marketMetrics?.sentiment),
      detail: `Capi ${formatUsdCompact(marketMetrics?.marketCap)}`,
    },
    {
      label: 'Portefeuille',
      value: formatCompactNumber(portfolioOverview?.portfolioValue),
      detail: portfolioOverview?.assetCount != null ? `${portfolioOverview.assetCount} actifs suivis` : 'Vue consolidÃƒÂ©e prÃƒÂªte',
    },
  ];

  const marketSummary = [
    {
      label: 'Dominance BTC',
      value: formatPercent(marketMetrics?.btcDominance, 2),
    },
    {
      label: 'Variation 24h',
      value: formatPercent(marketMetrics?.marketCap24hChange, 2),
    },
    {
      label: 'Confiance rÃƒÂ©gime',
      value: formatScore(marketMetrics?.regimeConfidence),
    },
  ];

  return (
    <div className="home-page">
      <div className="home-page__shell">
        <section className="home-hero">
          <div className="home-hero__copy">
            <div className="home-badge">
              <span className="home-badge__dot" />
              EcotoneAI, cockpit de dÃƒÂ©cision crypto
            </div>
            <h1 className="home-hero__title">
              Passez du bruit du marchÃƒÂ© ÃƒÂ  des dÃƒÂ©cisions plus lisibles.
            </h1>
            <p className="home-hero__subtitle">
              EcotoneAI relie le contexte macro, le scanning dÃ¢â‚¬â„¢opportunitÃƒÂ©s et le portefeuille dans un mÃƒÂªme flux de travail.
              LÃ¢â‚¬â„¢idÃƒÂ©e reste fidÃƒÂ¨le au mot ecotone : une zone de transition oÃƒÂ¹ deux mondes se rencontrent, sauf quÃ¢â‚¬â„¢ici le passage se fait entre donnÃƒÂ©es brutes et signaux exploitables.
            </p>

            <div className="home-hero__actions">
              <Link to="/dashboard" className="home-cta home-cta--primary">
                Ouvrir le dashboard
                <FiArrowRight />
              </Link>
              <Link to="/scanner" className="home-cta home-cta--secondary">
                Lancer le pattern scanner
              </Link>
              <Link to="/market" className="home-cta home-cta--ghost">
                Lire le marchÃƒÂ©
              </Link>
            </div>

            <div className="home-stack-list">
              {stackHighlights.map((item) => (
                <div key={item} className="home-stack-list__item">
                  <FiCpu />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="home-hero__panel">
            <div className="home-panel home-panel--primary">
              <div className="home-panel__header">
                <span className="home-panel__eyebrow">Pulse live</span>
                <span className="home-panel__timestamp">{formatRelativeDate(marketMetrics?.lastUpdate || marketMetrics?.timestamp)}</span>
              </div>
              <div className="home-pulse-grid">
                {heroPulse.map((item) => (
                  <div key={item.label} className="home-pulse-card">
                    <span className="home-pulse-card__label">{item.label}</span>
                    <strong className="home-pulse-card__value">{item.value}</strong>
                    <span className="home-pulse-card__detail">{item.detail}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="home-panel home-panel--secondary">
              <div className="home-panel__header">
                <span className="home-panel__eyebrow">Lecture du terrain</span>
                <span className="home-panel__status">{marketMetrics ? 'DonnÃƒÂ©es connectÃƒÂ©es' : 'Connexion en cours'}</span>
              </div>
              <div className="home-summary-strip">
                {marketSummary.map((item) => (
                  <div key={item.label} className="home-summary-strip__item">
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>
              <p className="home-panel__insight">
                {portfolioOverview?.topRisk?.title || 'La page d\'accueil est prÃƒÂªte pour servir de point dÃ¢â‚¬â„¢entrÃƒÂ©e opÃƒÂ©rationnel, mÃƒÂªme si certaines mÃƒÂ©triques live nÃ¢â‚¬â„¢ont pas encore rÃƒÂ©pondu.'}
              </p>
            </div>
          </div>
        </section>

        <section className="home-section">
          <div className="home-section__heading">
            <div>
              <span className="home-section__eyebrow">AccÃƒÂ¨s direct</span>
              <h2>Les modules qui comptent vraiment au quotidien</h2>
            </div>
            <p>
              La page dÃ¢â‚¬â„¢accueil doit vous faire entrer dans le bon ÃƒÂ©cran en quelques secondes, pas vous laisser devant un slogan vide.
            </p>
          </div>

          <div className="home-feature-grid">
            {primaryActions.map(({ title, description, path, Icon, tone }) => (
              <Link to={path} key={title} className={`home-feature-card home-feature-card--${tone}`}>
                <div className="home-feature-card__top">
                  <div className="home-feature-card__icon">
                    <Icon />
                  </div>
                  <span className="home-feature-card__link">
                    Ouvrir
                    <FiArrowRight />
                  </span>
                </div>
                <h3>{title}</h3>
                <p>{description}</p>
              </Link>
            ))}
          </div>
        </section>

        <section className="home-section home-section--workflow">
          <div className="home-section__heading">
            <div>
              <span className="home-section__eyebrow">MÃƒÂ©thode</span>
              <h2>Un flux pensÃƒÂ© pour arbitrer, pas pour empiler des widgets</h2>
            </div>
            <p>
              La sÃƒÂ©quence utile est simple : contexte, dÃƒÂ©tection, exÃƒÂ©cution. La home sert de rampe dÃ¢â‚¬â„¢accÃƒÂ¨s ÃƒÂ  ce trio.
            </p>
          </div>

          <div className="home-workflow-grid">
            {workflowSteps.map(({ title, description, Icon }, index) => (
              <div key={title} className="home-workflow-card">
                <div className="home-workflow-card__step">0{index + 1}</div>
                <div className="home-workflow-card__icon">
                  <Icon />
                </div>
                <h3>{title}</h3>
                <p>{description}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default HomePage;